// reportGenerator.ts
import type { Project, Item, User } from './types/core';
import type { Attachment } from './types/attachments';
import { getProjectReportHTMLWithResolver, escapeStringForTemplateLiteral } from './reportTemplate';

// Generates the HTML report with either external URLs or local ZIP paths
export const generateProjectHtmlReportContentWithPaths = (
  project: Project,
  projectNameForExport: string,
  itemsToExport: Item[],
  availablePurchasers: User[],
  reportCreationDate: string,
  t: (key: string, options?: any) => string,
  useLocalPaths: boolean = false
): string => {


  // Helper function to generate fund attachment path
  const getFundAttachmentPath = (attachment: Attachment, fundEntry: any, purchaserId: string): string => {
    if (!useLocalPaths) {
      return attachment.url || attachment.data || '';
    }

    // Generate local path for ZIP export (relative to reports/project_summary.html)
    const attachmentIndex = fundEntry.attachments?.findIndex((att: Attachment) => att.id === attachment.id) || 0;
    const sanitizedAttachmentFileName = `${attachmentIndex + 1}_${attachment.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;

    return `../attachments/fund_attachment/${purchaserId}/${fundEntry.id}/${sanitizedAttachmentFileName}`;
  };

  // Prepare display strings for project-level info
  const displayProjectName = String(projectNameForExport ?? '');
  const displayProjectDescription = String(project.description ?? ''); // Template has its own fallback, so empty string is fine if null/undefined

  const projectDeadlineFormatted = project.deadline ? new Date(project.deadline).toLocaleDateString('th-TH') : t('general.not.set', { defaultValue: 'N/A' });
  // This was already defined above, removing duplicate. // Correction: This is the one that should be kept.

  // Define getPurchaserDataForReport first
  const getPurchaserDataForReport = (purchaserId: string): { id: string; name: string; team: string; spent: number; allocated: number; items: any[] } => {
    const purchaserUser = availablePurchasers.find(p => p.id === purchaserId);
    const fundInfo = project.purchaserFunds.find(pf => pf.purchaserId === purchaserId);

    const isSuperseded = (item: Item) => {
      return item.status === 'approved' && !item.isPurchaseItem && itemsToExport.some(i => i.isPurchaseItem === true && String(i.originalItemId) === String(item.id));
    };

    const itemsPurchasedForDisplay = itemsToExport.filter(item => {
      const isBoughtOrUnsupervised = item.status === 'bought' || item.isUnsupervisedPurchase === true;
      const isApprovedAndValid = item.status === 'approved' && !isSuperseded(item);

      if (!item.isGroup && !item.parentId) { // Individual top-level item
        return item.purchaser === purchaserId && (isBoughtOrUnsupervised || isApprovedAndValid);
      }
      if (item.isGroup === true) { // Group item
        const itemIdStr = String(item.id);
        const isGroupDirectlyAssigned = item.purchaser === purchaserId;

        // Check filtering for children - use strict string comparison and check superseded
        const hasChildAssignedToThisPurchaserAndValid = project.items.some(childInProjectItems =>
          String(childInProjectItems.parentId) === itemIdStr &&
          childInProjectItems.purchaser === purchaserId &&
          (childInProjectItems.status === 'bought' || childInProjectItems.isUnsupervisedPurchase === true || (childInProjectItems.status === 'approved' && !isSuperseded(childInProjectItems)))
        );

        // Include group logic matching ProjectView
        return (isGroupDirectlyAssigned || hasChildAssignedToThisPurchaserAndValid) && project.items.some(child =>
          String(child.parentId) === itemIdStr &&
          (child.status === 'bought' || child.isUnsupervisedPurchase === true || (child.status === 'approved' && !isSuperseded(child)))
        );
      }
      return false;
    });

    let spentByPurchaser = 0;
    itemsPurchasedForDisplay.forEach(item => {
      let itemDisplayPrice = 0;
      if (item.isGroup) {
        // Check if group has groupTotalPrice set
        if (item.groupTotalPrice && item.groupTotalPrice > 0) {
          itemDisplayPrice = item.groupTotalPrice + (item.shippingCost || 0);
        } else {
          const itemIdStr = String(item.id);
          const approvedOrUnsupervisedChildren = project.items.filter(child =>
            String(child.parentId) === itemIdStr &&
            (child.status === 'bought' || child.isUnsupervisedPurchase === true || (child.status === 'approved' && !isSuperseded(child)))
          );
          const childrenTotalPrice = approvedOrUnsupervisedChildren.reduce((sum, child) => {
            return sum + ((child.price || 0) * child.quantity) + (child.shippingCost || 0);
          }, 0);
          itemDisplayPrice = childrenTotalPrice + (item.shippingCost || 0);
        }
      } else {
        itemDisplayPrice = ((item.price || 0) * item.quantity) + (item.shippingCost || 0);
      }
      spentByPurchaser += itemDisplayPrice;
    });

    const allocatedToPurchaser = (fundInfo?.allocatedAmount || 0) + (fundInfo?.additionalFunds || 0);

    const finalDisplayItems = itemsPurchasedForDisplay.map(item => {
      let originalItemName = String(item.name ?? '');
      if (item.isGroup) {
        originalItemName = String(item.groupName || originalItemName);
        if (!originalItemName) {
          originalItemName = `${t('item.unnamedGroup', { defaultValue: 'Unnamed Group' })} ${item.id}`;
        }
      } else {
        if (!originalItemName) {
          if (item.parentId) {
            const parentGroup = project.items.find(i => String(i.id) === String(item.parentId));
            const parentGroupName = parentGroup ? (parentGroup.groupName || parentGroup.name) : '';
            if (parentGroupName) {
              originalItemName = `${t('item.childInGroup', { defaultValue: 'Item in' })} '${escapeStringForTemplateLiteral(parentGroupName)}' (ID: ${item.id})`;
            } else {
              originalItemName = `${t('item.unnamedChild', { defaultValue: 'Unnamed Item' })} (ID: ${item.id})`;
            }
          } else {
            originalItemName = `${t('item.unnamedItem', { defaultValue: 'Unnamed Item' })} ${item.id}`;
          }
        }
      }
      const htmlDisplayName = originalItemName;

      let itemDisplayTotalPrice = 0;
      if (item.isGroup) {
        // Check if group has groupTotalPrice set
        if (item.groupTotalPrice && item.groupTotalPrice > 0) {
          itemDisplayTotalPrice = item.groupTotalPrice;
        } else {
          const itemIdStr = String(item.id);
          const approvedOrUnsupervisedChildren = project.items.filter(child =>
            String(child.parentId) === itemIdStr &&
            (child.status === 'bought' || child.isUnsupervisedPurchase === true || (child.status === 'approved' && !isSuperseded(child)))
          );
          // Include child shipping costs: children are not rendered as separate
          // rows, so this row's price column is the only place they can appear.
          // Without them the item rows don't add up to the "spent" header stat.
          const childrenTotalPrice = approvedOrUnsupervisedChildren.reduce((sum, child) => {
            return sum + ((child.price || 0) * child.quantity) + (child.shippingCost || 0);
          }, 0);
          itemDisplayTotalPrice = childrenTotalPrice;
        }
      } else {
        itemDisplayTotalPrice = ((item.price || 0) * item.quantity);
      }
      return {
        ...item,
        name: escapeStringForTemplateLiteral(htmlDisplayName),
        details: escapeStringForTemplateLiteral(item.details ?? ''),
        unit: escapeStringForTemplateLiteral(item.unit ?? ''),
        category: escapeStringForTemplateLiteral(item.category ?? ''),
        quantity: item.quantity,
        price: item.price, // This is the item's own unit price, might be 0 for groups
        totalPrice: itemDisplayTotalPrice, // This is the calculated "spent" value for the item/group row
        // Calculate effective status for groups
        status: (() => {
          if (item.isGroup) {
            const itemIdStr = String(item.id);
            const children = project.items.filter(child => String(child.parentId) === itemIdStr);
            const approvedChildren = children.filter(child => child.status === 'bought' || child.isUnsupervisedPurchase === true || (child.status === 'approved' && !isSuperseded(child)));
            // If group has children and all are approved/bought, show as approved
            if (children.length > 0 && approvedChildren.length === children.length) {
              return 'approved';
            }
          }
          return item.status;
        })(),
        // Calculate total attachments for groups
        totalAttachmentsCount: item.isGroup
          ? (item.attachments?.length || 0) + project.items.reduce((acc, child) => {
            const itemIdStr = String(item.id);
            if (String(child.parentId) === itemIdStr) {
              return acc + (child.attachments?.length || 0);
            }
            return acc;
          }, 0)
          : item.attachments?.length || 0,
      };
    });

    return {
      id: purchaserId,
      name: escapeStringForTemplateLiteral(purchaserUser?.fullName || t('general.unassigned', { defaultValue: 'Unassigned' })),
      team: escapeStringForTemplateLiteral(purchaserUser?.team || ''),
      spent: spentByPurchaser,
      allocated: allocatedToPurchaser,
      items: finalDisplayItems,
    };
  };

  const uniquePurchaserIdsInReportItems = Array.from(new Set(itemsToExport.map(item => item.purchaser).filter(Boolean))) as string[];
  const allFundedPurchaserIds = project.purchaserFunds.map(pf => pf.purchaserId);
  const allRelevantPurchaserIds = Array.from(new Set([...uniquePurchaserIdsInReportItems, ...allFundedPurchaserIds]));

  const processedPurchasersData = allRelevantPurchaserIds
    .map(id => getPurchaserDataForReport(id))
    .filter(pData => pData.items.length > 0 || pData.allocated > 0 || pData.spent > 0);

  const totalSpentOnProject = processedPurchasersData.reduce((sum, pData) => sum + pData.spent, 0);
  const totalDisplayedItemsCount = processedPurchasersData.reduce((sum, pData) => sum + pData.items.length, 0);

  const totalReceivedForProject = project.totalReceived || project.initialFund || 0;
  // For consistency, totalAllocatedToPurchasers in the summary should reflect the sum of 'allocated' from displayed purchasers.
  // However, the original logic sums from project.purchaserFunds directly, which might include users not displayed.
  // We'll keep the original logic for totalAllocated and totalAdditional for now, as they reflect overall project fund movements.
  const totalAllocatedToPurchasers = project.purchaserFunds.reduce((sum, pf) => sum + (pf.allocatedAmount || 0), 0);

  // Calculate separate totals for additional and returned funds
  const totalAdditionalFundsGiven = project.purchaserFunds.reduce((sum, pf) => {
    const additionalFunds = pf.additionalFunds || 0;
    return sum + (additionalFunds > 0 ? additionalFunds : 0);
  }, 0);
  const totalFundsReturned = project.purchaserFunds.reduce((sum, pf) => {
    const additionalFunds = pf.additionalFunds || 0;
    return sum + (additionalFunds < 0 ? Math.abs(additionalFunds) : 0);
  }, 0);

  const projectBudgetRemaining = totalReceivedForProject - totalSpentOnProject;

  const getPrecalculatedPurchaserData = (purchaserId: string) => {
    return processedPurchasersData.find(pData => pData.id === purchaserId);
  };

  // Generate fund entry attachments HTML - Grouped by purchaser with smart grid
  let fundAttachmentsHtml = '';

  // Group fund attachments by purchaser
  const fundAttachmentsByPurchaser = new Map<string, { images: { src: string; safeName: string; fundDescription: string }[]; otherFiles: { src: string; safeName: string; fundDescription: string }[]; purchaserName: string }>();

  project.purchaserFunds.forEach(pf => {
    const purchaserUser = availablePurchasers.find(u => u.id === pf.purchaserId);
    const purchaserName = purchaserUser?.fullName || pf.purchaserId;

    if (!fundAttachmentsByPurchaser.has(pf.purchaserId)) {
      fundAttachmentsByPurchaser.set(pf.purchaserId, { images: [], otherFiles: [], purchaserName });
    }

    const purchaserData = fundAttachmentsByPurchaser.get(pf.purchaserId)!;

    pf.fundEntries.forEach(fe => {
      if (fe.attachments && fe.attachments.length > 0) {
        fe.attachments.forEach((att: Attachment) => {
          const src = getFundAttachmentPath(att, fe, pf.purchaserId);
          const safeName = escapeStringForTemplateLiteral(att.name);
          const fundDescription = escapeStringForTemplateLiteral(fe.description);

          if (att.type === 'application/pdf') {
            // Skip PDFs - they're in a separate folder
            return;
          } else if (att.type && att.type.startsWith('image/')) {
            purchaserData.images.push({ src, safeName, fundDescription });
          } else {
            purchaserData.otherFiles.push({ src, safeName, fundDescription });
          }
        });
      }
    });
  });

  // Generate HTML for each purchaser's fund attachments
  fundAttachmentsByPurchaser.forEach((data) => {
    if (data.images.length === 0 && data.otherFiles.length === 0) return;

    const imageCount = data.images.length;
    let gridClass = 'smart-image-grid';
    if (imageCount === 1) {
      gridClass += ' single-image';
    } else if (imageCount === 2) {
      gridClass += ' two-images';
    } else if (imageCount <= 4) {
      gridClass += ' few-images';
    } else {
      gridClass += ' many-images';
    }

    fundAttachmentsHtml += `<div class="item-attachments-page page-break">
      <div class="item-attachments-header">
        <div class="item-attachments-title">${t('fund.documents', { defaultValue: 'เอกสารการเงิน' })}</div>
        <div class="item-attachments-subtitle">${data.purchaserName} • ${imageCount} ${t('item.images', { defaultValue: 'รูปภาพ' })}${data.otherFiles.length > 0 ? ` • ${data.otherFiles.length} ${t('item.otherFiles', { defaultValue: 'ไฟล์อื่น' })}` : ''}</div>
      </div>
      <div class="${gridClass}">`;

    // Add all fund images in the grid
    for (const img of data.images) {
      fundAttachmentsHtml += `
        <div class="grid-image-container">
          <img src="${img.src}" alt="${img.safeName}" class="grid-image" />
          <div class="image-label">${img.fundDescription}</div>
        </div>`;
    }

    fundAttachmentsHtml += `</div>`;

    // Add other files as a list below the grid
    if (data.otherFiles.length > 0) {
      fundAttachmentsHtml += `<div class="other-files-list">`;
      for (const file of data.otherFiles) {
        fundAttachmentsHtml += `
          <div class="other-file-item">
            <a href="${file.src}" download="${file.safeName}">📎 ${file.safeName} (${file.fundDescription})</a>
          </div>`;
      }
      fundAttachmentsHtml += `</div>`;
    }

    fundAttachmentsHtml += `</div>`;
  });

  // Collect item attachments grouped by purchaser and item for proper ordering
  let separateAttachmentsHtml = '';

  // Process attachments grouped by purchaser, then by item - 1 ITEM PER PAGE with smart grid
  for (const pData of processedPurchasersData) {
    if (!pData || !pData.items || pData.items.length === 0) continue;

    // Get the original purchaser name (unescaped) for path matching
    const originalPurchaserName = availablePurchasers.find(p => p.id === pData.id)?.fullName || 'Unassigned';

    // For each item of this purchaser
    for (const item of pData.items) {
      // Find the original item from project.items (with unescaped name)
      const originalItem = project.items.find(i => i.id === item.id) || itemsToExport.find(i => i.id === item.id);
      const originalItemName = originalItem?.name || item.name;

      // Collect attachments separately by type: proof_of_purchase (1 per page) vs product_picture (combined)
      const proofOfPurchaseImages: { src: string; safeName: string; isChild: boolean; childName?: string; attachmentType: string }[] = [];
      const productPictureImages: { src: string; safeName: string; isChild: boolean; childName?: string; attachmentType: string }[] = [];
      const itemOtherFiles: { src: string; safeName: string; isChild: boolean; childName?: string }[] = [];

      // Process the item's own attachments first - ONLY include proof_of_purchase and product_picture types
      if (Array.isArray(item.attachments) && item.attachments.length > 0) {
        for (const att of item.attachments) {
          // Filter: only include proof_of_purchase and product_picture types in export
          const attType = att.attachmentType || 'general';
          if (attType !== 'proof_of_purchase' && attType !== 'product_picture') {
            continue; // Skip general attachments
          }

          // Build path using original unescaped names
          let src = '';
          if (!useLocalPaths) {
            src = att.url || att.data || '';
          } else {
            const sanitizedPurchaserName = originalPurchaserName.replace(/[^\u007F\p{L}\p{M}\p{N}\s-]/gu, '_').replace(/\s+/g, '_');
            const sanitizedItemName = originalItemName.replace(/[^\u007F\p{L}\p{M}\p{N}\s-]/gu, '_').replace(/\s+/g, '_');
            const attachmentIndex = item.attachments?.findIndex((a: Attachment) => a.id === att.id) || 0;
            const sanitizedAttachmentFileName = `${attachmentIndex + 1}_${att.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
            src = `../attachments/${sanitizedPurchaserName}/${sanitizedItemName}/${sanitizedAttachmentFileName}`;
          }

          const safeName = escapeStringForTemplateLiteral(att.name);

          if (att.type && att.type.startsWith('image/')) {
            // Separate by attachment type
            if (attType === 'proof_of_purchase') {
              proofOfPurchaseImages.push({ src, safeName, isChild: false, attachmentType: attType });
            } else if (attType === 'product_picture') {
              productPictureImages.push({ src, safeName, isChild: false, attachmentType: attType });
            }
          } else if (att.type === 'application/pdf') {
            // Skip PDFs completely
            continue;
          } else {
            itemOtherFiles.push({ src, safeName, isChild: false });
          }
        }
      }

      // If this is a group item, also collect child item attachments
      if (item.isGroup && project.items) {
        const itemIdStr = String(item.id);
        const childItems = project.items.filter((child: Item) => String(child.parentId) === itemIdStr);
        for (const childItem of childItems) {
          if (Array.isArray(childItem.attachments) && childItem.attachments.length > 0) {
            for (const att of childItem.attachments) {
              // Filter: only include proof_of_purchase and product_picture types in export
              const attType = att.attachmentType || 'general';
              if (attType !== 'proof_of_purchase' && attType !== 'product_picture') {
                continue; // Skip general attachments
              }

              // Build path using original unescaped names for child items
              let src = '';
              if (!useLocalPaths) {
                src = att.url || att.data || '';
              } else {
                const sanitizedPurchaserName = originalPurchaserName.replace(/[^\u007F\p{L}\p{M}\p{N}\s-]/gu, '_').replace(/\s+/g, '_');
                const sanitizedGroupName = originalItemName.replace(/[^\u007F\p{L}\p{M}\p{N}\s-]/gu, '_').replace(/\s+/g, '_');
                const sanitizedChildName = `${childItem.name.replace(/[^\u007F\p{L}\p{M}\p{N}\s-]/gu, '_').replace(/\s+/g, '_')}_child`;
                const attachmentIndex = childItem.attachments?.findIndex((a: Attachment) => a.id === att.id) || 0;
                const sanitizedAttachmentFileName = `${attachmentIndex + 1}_${att.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
                src = `../attachments/${sanitizedPurchaserName}/${sanitizedGroupName}/${sanitizedChildName}/${sanitizedAttachmentFileName}`;
              }

              const safeName = escapeStringForTemplateLiteral(att.name);
              const childItemName = escapeStringForTemplateLiteral(childItem.name);

              if (att.type && att.type.startsWith('image/')) {
                // Separate by attachment type
                if (attType === 'proof_of_purchase') {
                  proofOfPurchaseImages.push({ src, safeName, isChild: true, childName: childItemName, attachmentType: attType });
                } else if (attType === 'product_picture') {
                  productPictureImages.push({ src, safeName, isChild: true, childName: childItemName, attachmentType: attType });
                }
              } else if (att.type === 'application/pdf') {
                // Skip PDFs completely
                continue;
              } else {
                itemOtherFiles.push({ src, safeName, isChild: true, childName: childItemName });
              }
            }
          }
        }
      }

      const itemName = escapeStringForTemplateLiteral(item.name);
      const purchaserName = escapeStringForTemplateLiteral(pData.name);

      // SECTION 1: Render proof of purchase images - 1 image per page
      for (const img of proofOfPurchaseImages) {
        const labelText = img.isChild && img.childName ? ` - ${img.childName}` : '';
        separateAttachmentsHtml += `<div class="item-attachments-page page-break">
          <div class="item-attachments-header">
            <div class="item-attachments-title">${t('attachment.type.proof_of_purchase', { defaultValue: 'หลักฐานการชำระเงิน' })} - ${itemName}${labelText}</div>
            <div class="item-attachments-subtitle">${purchaserName}</div>
          </div>
          <div class="smart-image-grid single-image">
            <div class="grid-image-container">
              <img src="${img.src}" alt="${img.safeName}" class="grid-image" />
              <div class="image-label">${img.safeName}</div>
            </div>
          </div>
        </div>`;
      }

      // SECTION 2: Render product pictures - combined on grid pages
      if (productPictureImages.length > 0) {
        // Determine grid layout based on number of images
        const imageCount = productPictureImages.length;
        let gridClass = 'smart-image-grid';
        if (imageCount === 1) {
          gridClass += ' single-image';
        } else if (imageCount === 2) {
          gridClass += ' two-images';
        } else if (imageCount <= 4) {
          gridClass += ' few-images';
        } else {
          gridClass += ' many-images';
        }

        // Start item page container for product pictures
        separateAttachmentsHtml += `<div class="item-attachments-page page-break">
          <div class="item-attachments-header">
            <div class="item-attachments-title">${t('attachment.type.product_picture', { defaultValue: 'รูปภาพสินค้า' })} - ${itemName}</div>
            <div class="item-attachments-subtitle">${purchaserName} • ${imageCount} ${t('item.images', { defaultValue: 'รูปภาพ' })}</div>
          </div>
          <div class="${gridClass}">`;

        // Add all product pictures in the grid
        for (const img of productPictureImages) {
          const labelText = img.isChild && img.childName ? `${img.childName}` : img.safeName;
          separateAttachmentsHtml += `
            <div class="grid-image-container">
              <img src="${img.src}" alt="${img.safeName}" class="grid-image" />
              <div class="image-label">${labelText}</div>
            </div>`;
        }

        separateAttachmentsHtml += `</div>`;

        // Close product pictures page container
        separateAttachmentsHtml += `</div>`;
      }

      // SECTION 3: Render other files (if any)
      if (itemOtherFiles.length > 0) {
        separateAttachmentsHtml += `<div class="item-attachments-page page-break">
          <div class="item-attachments-header">
            <div class="item-attachments-title">${t('item.otherFiles', { defaultValue: 'ไฟล์อื่น' })} - ${itemName}</div>
            <div class="item-attachments-subtitle">${purchaserName} • ${itemOtherFiles.length} ${t('item.otherFiles', { defaultValue: 'ไฟล์อื่น' })}</div>
          </div>
          <div class="other-files-list">`;

        for (const file of itemOtherFiles) {
          const labelText = file.isChild && file.childName ? ` (${file.childName})` : '';
          separateAttachmentsHtml += `
            <div class="other-file-item">
              <a href="${file.src}" download="${file.safeName}">📎 ${file.safeName}${labelText}</a>
            </div>`;
        }

        separateAttachmentsHtml += `</div></div>`;
      }
    }
  }

  return getProjectReportHTMLWithResolver(
    displayProjectName,
    displayProjectDescription,
    reportCreationDate,
    totalDisplayedItemsCount,      // Arg 5: For "Project Summary" card "Items purchased" & footer
    totalSpentOnProject,           // Arg 8
    project.teamsInvolved,         // Arg 9: Changed from length to actual team names array
    projectDeadlineFormatted,      // Arg 10
    totalReceivedForProject,       // Arg 11
    totalAllocatedToPurchasers,    // Arg 12
    projectBudgetRemaining,        // Arg 14
    processedPurchasersData.map(pData => pData.id), // Arg 16: displayedPurchaserIds
    getPrecalculatedPurchaserData, // Arg 17: Function to get data for a specific purchaser
    t,                             // Arg 19
    fundAttachmentsHtml,           // Arg 20: Fund entry attachments HTML
    totalAdditionalFundsGiven,     // Arg 23: Separate additional funds given
    totalFundsReturned,            // Arg 24: Separate funds returned
    separateAttachmentsHtml        // Arg 25: Separate attachments section
  );
};
